import React, { useState } from 'react';
import { Select, SelectItem, Selection, Input } from '@nextui-org/react';
import { MarketData } from 'common/src/utils/types';
import { useTranslation } from '../../hooks/useTranslation';
import { MARKETS, MARKET_LABELS } from '../../utils/definitions';

interface RegionFilterProps {
  data: MarketData[];
  selectedRegion: string | string[];
  onSelectionChange: (value: string[]) => void;
  selectionMode?: 'single' | 'multiple';
  label?: string;
  className?: string;
  size?: 'sm' | 'md' | 'lg';
}

const RegionFilter: React.FC<RegionFilterProps> = ({
  data,
  selectedRegion,
  onSelectionChange,
  selectionMode = 'single',
  label,
  className = 'min-w-32',
  size = 'sm',
}) => {
  const { t } = useTranslation('common');
  const finalLabel = label || t('Mercado de Venta');
  const [search, setSearch] = useState('');

  // Use MARKETS constant (excluding 'all')
  const regions = MARKETS.filter((m) => m !== 'all');
  const lowerSearch = search.toLowerCase();
  const filteredRegions = search
    ? regions.filter((r) => (MARKET_LABELS[r] || r).toLowerCase().includes(lowerSearch))
    : regions;

  if (regions.length === 0) {
    return null;
  }

  const handleSelectionChange = (keys: Selection) => {
    if (selectionMode === 'multiple') {
      const keysArray = Array.from(keys);

      if (keysArray.includes('all')) {
        // If 'all' is selected and there were previous selections, clear all
        if (Array.isArray(selectedRegion) && selectedRegion.length > 0) {
          onSelectionChange([]);
        } else {
          // Otherwise, select all regions
          onSelectionChange(regions);
        }
      } else {
        // Normal selection behavior for individual items
        onSelectionChange(keysArray as string[]);
      }
    } else {
      // Single selection
      const selectedKeys = Array.from(keys);
      onSelectionChange(selectedKeys as string[]);
    }
  };

  return (
    <Select
      label={finalLabel}
      selectedKeys={
        selectionMode === 'multiple'
          ? Array.isArray(selectedRegion)
            ? new Set(selectedRegion)
            : new Set([])
          : new Set([selectedRegion as string])
      }
      onSelectionChange={handleSelectionChange}
      selectionMode={selectionMode}
      className={className}
      size={size}
      aria-label={`${t('Selecciona mercado de venta')}`}
      renderValue={(items) => {
        if (
          Array.isArray(selectedRegion) &&
          selectedRegion.length === regions.length &&
          selectedRegion.length > 0
        ) {
          return (
            <div className='flex items-center gap-2'>{t('Todos los mercados')}</div>
          );
        }
        if (Array.isArray(selectedRegion) && selectedRegion.length > 0) {
          // Show first 2 regions with labels and +N more
          const displayCount = 2;
          const firstRegions = selectedRegion.slice(0, displayCount);
          const remaining = selectedRegion.length - displayCount;

          return (
            <div className='flex items-center gap-2'>
              {firstRegions.map(r => MARKET_LABELS[r] || r).join(', ')}
              {remaining > 0 && ` +${remaining}`}
            </div>
          );
        }
        return items;
      }}
      listboxProps={{
        topContent: (
          <div className='sticky top-0 z-10 bg-content1 px-1 pt-1 pb-1'>
            <Input
              size='sm'
              placeholder={t('Buscar mercado...')}
              value={search}
              onValueChange={setSearch}
              isClearable
              onClear={() => setSearch('')}
            />
          </div>
        ),
      }}
    >
      {selectionMode === 'multiple' && (
        <SelectItem key='all' value='all' textValue={t('Todos')}>
          <div className='flex justify-between items-center'>
            <span>
              {Array.isArray(selectedRegion) && selectedRegion.length > 0
                ? t('Deseleccionar todos')
                : t('Seleccionar todos')}
            </span>
          </div>
        </SelectItem>
      )}
      {filteredRegions.map((region) => (
        <SelectItem key={region} value={region} textValue={MARKET_LABELS[region] || region}>
          {MARKET_LABELS[region] || region}
        </SelectItem>
      ))}
    </Select>
  );
};

export default React.memo(RegionFilter);
